import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Box, Text, Flex, SimpleGrid, Spinner, HStack, Breadcrumb, Input } from "@chakra-ui/react";
import { MdCategory, MdChevronRight, MdDelete, MdEdit, MdLocalOffer } from "react-icons/md";
import { Button } from "components/ui/Button";
import FormDialog from "components/utils/FormDialog";
import { useDialogForm } from "utils/useDialogForm";
import {
  useDashboardTag,
  useDeleteDashboardTag,
  usePatchDashboardTag,
} from "features/dashboard/tags/hooks";

const TagDetail = () => {
  const { id: tagId } = useParams();
  const navigate = useNavigate();
  const tagQ = useDashboardTag(tagId);
  const patchTag = usePatchDashboardTag();
  const deleteTag = useDeleteDashboardTag();
  const editDialog = useDialogForm({ nombre: "" });

  const tag = tagQ.data;
  const categorias = Array.isArray(tag?.categorias) ? tag.categorias : [];
  const crumbTitle = tag?.nombre ?? "Tag";

  const onEditOpen = () => {
    editDialog.openWith({ nombre: tag?.nombre ?? "" });
  };

  const onEditSave = async () => {
    const nombre = editDialog.form.nombre.trim();
    if (!nombre) return;
    try {
      await patchTag.mutateAsync({ tagId, patch: { nombre } });
      editDialog.close();
    } catch (_e) {}
  };

  const onDelete = async () => {
    if (!window.confirm("Eliminar este tag?")) return;
    try {
      await deleteTag.mutateAsync(tagId);
      navigate("/dashboard/club/tags");
    } catch (_e) {}
  };

  return (
    <Box>
      <Breadcrumb.Root pb="2">
        <Breadcrumb.List>
          <Breadcrumb.Item>
            <Breadcrumb.Link asChild>
              <Link to="/dashboard/club/tags">
                <HStack gap="1">
                  <MdLocalOffer />
                  <Text>Tags</Text>
                </HStack>
              </Link>
            </Breadcrumb.Link>
          </Breadcrumb.Item>
          <Breadcrumb.Separator>
            <MdChevronRight />
          </Breadcrumb.Separator>
          <Breadcrumb.Item>
            <Breadcrumb.CurrentLink>{crumbTitle}</Breadcrumb.CurrentLink>
          </Breadcrumb.Item>
        </Breadcrumb.List>
      </Breadcrumb.Root>

      <Flex
        align={{ base: "flex-start", md: "center" }}
        justify="space-between"
        mb="6"
        gap="4"
        direction={{ base: "column", sm: "row" }}
      >
        <Flex align="center" gap="3">
          <Box
            w="48px"
            h="48px"
            borderRadius="14px"
            bg="teal.500"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
          >
            <MdLocalOffer size="24px" color="white" />
          </Box>
          <Box>
            <Text
              fontFamily="display"
              fontWeight="700"
              fontSize={{ base: "2xl", md: "3xl" }}
              color="dashboard.fg.default"
              lineHeight="1.1"
            >
              {crumbTitle}
            </Text>
            <Text fontSize="sm" color="dashboard.fg.muted" mt="0.5">
              {categorias.length} {categorias.length === 1 ? "categoria asignada" : "categorias asignadas"}
            </Text>
          </Box>
        </Flex>

        <HStack gap="2" alignSelf={{ base: "stretch", sm: "auto" }}>
          <Button variant="solid" color="red" onClick={onEditOpen} radius="xl" flex={{ base: 1, sm: "initial" }}>
            <MdEdit />
            Editar nombre
          </Button>
          <Button
            variant="outline"
            color="dark"
            onClick={onDelete}
            radius="xl"
            disabled={deleteTag.isPending}
            flex={{ base: 1, sm: "initial" }}
          >
            <MdDelete />
            Eliminar
          </Button>
        </HStack>
      </Flex>

      {tagQ.isLoading && (
        <Flex justify="center" align="center" py="12">
          <Spinner />
        </Flex>
      )}

      {tagQ.isError && (
        <Box
          bg="dashboard.bg.surface"
          borderWidth="1px"
          borderColor="dashboard.border.muted"
          rounded="card"
          p="8"
        >
          <Text color="dashboard.fg.default" fontWeight="bold">
            Tag no encontrado
          </Text>
          <Text color="dashboard.fg.muted" fontSize="sm" mt="1">
            Revisa que el periodo seleccionado sea el correcto.
          </Text>
          <Button variant="outline" color="dark" mt="4" radius="xl" onClick={() => navigate("/dashboard/club/tags")}>
            Ver todos los tags
          </Button>
        </Box>
      )}

      {!tagQ.isLoading && !tagQ.isError && categorias.length === 0 && (
        <Box
          bg="dashboard.bg.surface"
          borderWidth="1px"
          borderColor="dashboard.border.muted"
          rounded="card"
          p="10"
          textAlign="center"
        >
          <Text color="dashboard.fg.muted">Este tag no esta asignado a ninguna categoria.</Text>
        </Box>
      )}

      {!tagQ.isLoading && categorias.length > 0 && (
        <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} gap="4">
          {categorias.map((categoria) => (
            <Box
              key={categoria.id}
              bg="dashboard.bg.surface"
              borderWidth="1px"
              borderColor="dashboard.border.muted"
              rounded="card"
              p="5"
              cursor="pointer"
              transition="transform 150ms ease, box-shadow 150ms ease"
              _hover={{ transform: "translateY(-2px)", boxShadow: "cardLift" }}
              onClick={() => navigate(`/dashboard/club/categorias/${categoria.id}`)}
            >
              <Flex align="center" gap="4">
                <Box
                  w="48px"
                  h="48px"
                  borderRadius="14px"
                  bg="purple.500"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                >
                  <MdCategory size="24px" color="white" />
                </Box>
                <Box flex={1} minW={0}>
                  <Text
                    fontFamily="display"
                    fontWeight="700"
                    fontSize="md"
                    color="dashboard.fg.default"
                    lineClamp={1}
                  >
                    {categoria.nombre}
                  </Text>
                </Box>
                <MdChevronRight
                  size="20px"
                  style={{ color: "var(--chakra-colors-dashboard-fg-subtle)", flexShrink: 0 }}
                />
              </Flex>
            </Box>
          ))}
        </SimpleGrid>
      )}

      <FormDialog
        open={editDialog.open}
        onOpenChange={editDialog.onOpenChange}
        title="Editar tag"
        saving={patchTag.isPending}
        onCancel={editDialog.close}
        onSave={onEditSave}
        saveLabel="Guardar"
        errorText={patchTag.isError ? String(patchTag.error?.message || patchTag.error) : ""}
      >
        <Input
          placeholder="Nombre del tag"
          bg="dashboard.bg.input"
          borderColor="dashboard.border.default"
          {...editDialog.bind("nombre")}
          onKeyDown={(e) => e.key === "Enter" && onEditSave()}
        />
      </FormDialog>
    </Box>
  );
};

export default TagDetail;
